// alternative-dashboard/alternative-components/approval/RecentTransactionPanel.tsx
import { useState, useEffect } from "react";
import { gxIsArray } from "@/app/utils/helpers";
import type { BeneficiaryData } from "@/app/types/beneficiaries";
import type { GridRowClickEvent } from "@progress/kendo-react-grid";
import type { RecentTransactionsResponse } from "@/app/types/payments";
import type { DashboardPendingRepairViewSummaryResponse } from "@/app/types/alterNativeDashboardLayout";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { DetailsComponent } from "../../../Dashboard/PaymentDetails/DetailsComponent";
import { gxDashboardPendingRepairViewSummaryApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import BeneficiaryView from "../../../beneficiaries/manageBeneficiary/BeneficiaryView";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import SuccessModalSM from "@/app/models/dialogs/SuccessModalSM";
import { usePaymentApprovalFlow } from "../../types/usePaymentApprovalFlow";
import type { BulkFileRow } from "../../../bulk-uploads/BulkUpload";
import {
  gxPaymentsBulkViewSummaryApi,
  gxPayrollViewSummaryApi,
} from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import RetryPayroll from "../../../Dashboard/transactions/RetryPayroll";
import RetryBulkPayment from "../../../Dashboard/transactions/RetryBulkPayment";
import RepairTransactionTable from "./RepairTransactionTable";
import { t } from "i18next";

interface RecentTransactionProps {
  title?: string;
  callRecent: boolean;
  callCount: boolean;
}

type RecentTransactionRow = RecentTransactionsResponse & {
  moduleType: string;
  recordId: number;
  paymentDate: string;
  status?: string;
  paymentStatusCode?: number;
  recordStatusCodeDesc?: string;
};

const RecentTransactionPanel = ({
  callRecent,
  callCount,
}: RecentTransactionProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<
    DashboardPendingRepairViewSummaryResponse[]
  >([]);

  const [selectedBulkRow, setSelectedBulkRow] = useState<BulkFileRow | null>(
    null,
  );
  const [showBulkRetryModal, setShowBulkRetryModal] = useState(false);
  const [showPayrollRetryModal, setShowPayrollRetryModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const rtpFlow = usePaymentApprovalFlow("RTP");
  const txnFlow = usePaymentApprovalFlow("TRANS");
  const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<BeneficiaryData | null>(null);

  const fetchRecentTransactions = async () => {
    try {
      setLoading(true);
      const result = await gxDashboardPendingRepairViewSummaryApi({
        currencyCode: "",
      });

      if (gxIsArray(result?.data) && result?.data?.length > 0) {
        setTransactions(result.data);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      setTransactions([]);
      console.log("Error in fetchRecentTransactions::", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (callCount) fetchRecentTransactions();
  }, [callCount]);

  useEffect(() => {
    if (callRecent) fetchRecentTransactions();
  }, [callRecent]);

  // ---------------- helper mapper ----------------
  const mapApiResponseToRow = (item: any): BulkFileRow => ({
    growxRefNo: item.gxJobChnNo || "--",
    uploadedOn: item.crtdDt || "--",
    totalTxn: item.totalTxnCount ?? "--",
    debitAmount: item.totalAmount ?? "--",
    validTxn: item.validTxnCount ?? "--",
    invalidTxn: item.invalidTxnCount ?? "--",
    pendingTxn: item.pendingTxnCount ?? "--",
    status: String(item.jobStatusCode || "--"),
    currency: item.baseCurrency || "--",
    jobStatusDesc: item.jobStatusDesc || "--",
    gxJobProcessid: item.gxJobProcessid,
    gxReferenceNo: item.gxReferenceNo,
    gxJobChnNo: item.gxJobChnNo,
    rejectNotes: item.rejectNotes || null,
    fileFormat: item.fileFormat || null,
  });

  // --- fetch helper ----------------------
  const fetchBulkRow = async (
    gxChnNo: string,
    type: "PAYMENT" | "PAYROLL",
  ): Promise<BulkFileRow | null> => {
    try {
      setBulkLoading(true);

      const response =
        type === "PAYMENT"
          ? await gxPaymentsBulkViewSummaryApi({
              pageNo: 1,
              pageSize: 100,
              searchText: gxChnNo,
              id: 4547,
            })
          : await gxPayrollViewSummaryApi({
              pageNo: 1,
              pageSize: 100,
              searchText: gxChnNo,
              id: 7547,
            });

      if (response?.status !== 1 || !Array.isArray(response.data)) {
        return null;
      }

      const row = response.data.find((x: any) => x.gxJobChnNo === gxChnNo);

      return row ? mapApiResponseToRow(row) : null;
    } catch (error) {
      console.error("fetchBulkRow error:", error);
      return null;
    } finally {
      setBulkLoading(false);
    }
  };

  const handleRowClick = async (event: GridRowClickEvent) => {
    const item = event.dataItem as RecentTransactionRow;

    const moduleType = item.moduleType?.trim()?.toUpperCase();

    switch (moduleType) {
      case "BENEFICIARY":
        setSelectedBeneficiary({
          beneId: item.recordId,
        } as BeneficiaryData);
        setOpenDetailsDrawer(true);
        break;

      case "RTP":
        rtpFlow.selectTransaction({
          transId: item.recordId,
          gxChnNo: item.gxChnNo,
        } as any);
        break;

      case "TRANSACTIONS":
        txnFlow.selectTransaction({
          transId: item.recordId,
          gxChnNo: item.gxChnNo,
        } as any);
        break;

      case "BULK": {
        const row = await fetchBulkRow(item.gxChnNo, "PAYMENT");

        if (!row) return;

        setSelectedBulkRow(row);
        setShowBulkRetryModal(true);
        break;
      }

      case "PAYROLL": {
        const row = await fetchBulkRow(item.gxChnNo, "PAYROLL");

        if (!row) return;

        setSelectedBulkRow(row);
        setShowPayrollRetryModal(true);
        break;
      }

      default:
        console.warn("Unknown moduleType:", moduleType);
    }
  };

  const mapToRecentTransactionRow = (
    item: DashboardPendingRepairViewSummaryResponse,
  ): RecentTransactionRow => ({
    recordId: item.recordId,
    moduleType: item.moduleType,
    paymentDate: item.paymentDate,

    transId: item.recordId,
    beneAccountName: item.beneAccountName ?? "",
    beneAccountNo: "",
    beneBankCountryCode2: null,
    beneId: 0,
    cbsAccountId: "",
    crtdBy: 0,
    crtdDt: item.paymentDate,

    debitAccountName: "",
    debitAccountNo: "",
    debitAccountType: "",

    debitAmount: item.debitAmount,
    debitBankName: item.bankName ?? "",
    debitCountryCode2: "",
    debitCurrency: item.debitCurrency ?? "",
    debitDenomination: 0,

    gxChnNo: item.gxChnNo,

    paymentCategory: "",
    paymentStatusDesc: item.statusDesc ?? "",

    paymentTypeId: 0,
    paymentTypeKey: "",
    paymentTypeName: null,
    purposeCodeName: null,
    soExeFrequency: null,

    sourceTable: item.moduleType,

    updtBy: 0,
    updtDt: null,

    shortForm:
      item.beneAccountName?.split(" ")[0]?.toUpperCase()?.slice(0, 3) ?? "",

    longName: null,
    companyName: item.beneAccountName,
    cbsReferenceNo: null,
    cbsResponseDate: null,
    debitAccountId: null,
    accountId: null,

    status: item.statusDesc ?? "",
    paymentStatusCode: Number(item.statusCode),
    recordStatusCodeDesc: item.statusDesc,
  });

  return (
    <div className="gx-repair-transaction-table">
      <div id="dashboardtable" className="gx-dashboard-table-header-alter">
        <div>
          <h5 className="gx-h5-m">{t("repair_transactions")}</h5>
        </div>

        <div className="relative">
          {loading && (
            <div className="gx-bene-form-container-loading-overlay">
              <SubmitLoading />
            </div>
          )}

          {!loading &&
            (transactions?.length > 0 ? (
              <RepairTransactionTable
                handleRowClick={handleRowClick}
                transactions={transactions.map(mapToRecentTransactionRow)}
              />
            ) : (
              <div className="flex justify-center w-full">
                <NoDataFound message="No Recent Transaction Found" />
              </div>
            ))}
        </div>
      </div>

      {openDetailsDrawer && selectedBeneficiary && (
        <DrawerNoFooter
          title={t("beneficiary")}
          subtitle={t("details")}
          openDrawer={openDetailsDrawer}
          onClose={() => {
            setOpenDetailsDrawer(false);
            setSelectedBeneficiary(null);
          }}
        >
          <BeneficiaryView
            rowdata={selectedBeneficiary}
            showTransactions={false}
          />
        </DrawerNoFooter>
      )}

      <DrawerNoFooter
        title={t("approval")}
        subtitle={t("request")}
        openDrawer={rtpFlow.openApprovalDrawer}
        onClose={() => rtpFlow.setOpenApprovalDrawer(false)}
      >
        <DetailsComponent
          transactionDetailsLoader={rtpFlow.transactionDetailsLoader}
          transaction={rtpFlow.transactionDetails}
          handleReject={rtpFlow.handleReject}
          handleSentForRepair={rtpFlow.handleReject}
          handleApprove={rtpFlow.handleApprove}
          schedule={false}
          showEdit={false}
          fromRtp={true}
        />
      </DrawerNoFooter>

      {rtpFlow.openRejectAndNoteModal && (
        <RemarksModal
          isOpen={rtpFlow.openRejectAndNoteModal}
          onClose={rtpFlow.closeRemark}
          errors={{}}
          setValue={() => {}}
          isReject={rtpFlow.isReject}
          value={rtpFlow.remark}
          onChange={rtpFlow.setRemark}
          submit={rtpFlow.submitReject}
        />
      )}

      {rtpFlow.openSuccessModal && (
        <SuccessModalSM
          onClose={rtpFlow.closeSuccessModal}
          isOpen={rtpFlow.openSuccessModal}
          successData={rtpFlow.successData}
        />
      )}

      {rtpFlow.isLoading && <SubmitLoading />}

      <DrawerNoFooter
        title={t("approval")}
        subtitle={t("request")}
        openDrawer={txnFlow.openApprovalDrawer}
        onClose={() => txnFlow.setOpenApprovalDrawer(false)}
      >
        <DetailsComponent
          transactionDetailsLoader={txnFlow.transactionDetailsLoader}
          transaction={txnFlow.transactionDetails}
          handleReject={txnFlow.handleReject}
          handleSentForRepair={txnFlow.handleReject}
          handleApprove={txnFlow.handleApprove}
          schedule={false}
          showEdit={false}
          fromRtp={false}
        />
      </DrawerNoFooter>

      {txnFlow.openRejectAndNoteModal && (
        <RemarksModal
          isOpen={txnFlow.openRejectAndNoteModal}
          onClose={txnFlow.closeRemark}
          errors={{}}
          setValue={() => {}}
          isReject={txnFlow.isReject}
          value={txnFlow.remark}
          onChange={txnFlow.setRemark}
          submit={txnFlow.submitReject}
        />
      )}

      {txnFlow.openSuccessModal && (
        <SuccessModalSM
          onClose={txnFlow.closeSuccessModal}
          isOpen={txnFlow.openSuccessModal}
          successData={txnFlow.successData}
        />
      )}

      {txnFlow.isLoading && <SubmitLoading />}

      {showBulkRetryModal && selectedBulkRow && (
        <RetryBulkPayment
          isOpenBulkList={showBulkRetryModal}
          setIsOpenBulkList={setShowBulkRetryModal}
          selectedRow={selectedBulkRow}
          bulkType="PAYMENT"
          access={{ view: true, approve: true }}
          setCallCount={() => {}}
        />
      )}

      {showPayrollRetryModal && selectedBulkRow && (
        <RetryPayroll
          isOpenBulkList={showPayrollRetryModal}
          setIsOpenBulkList={setShowPayrollRetryModal}
          selectedRow={selectedBulkRow}
          bulkType="PAYROLL"
          access={{ view: true, approve: true }}
          setCallCount={() => {}}
        />
      )}

      {bulkLoading && <SubmitLoading />}
    </div>
  );
};

export default RecentTransactionPanel;
